/// <amd-module name="WorkItemsHub/Utils/Array" />
/**
 * Compares two arrays for member-wise equality.
 *
 * @param arrayA First array to compare.
 * @param arrayB Other array to compare.
 * @return True if both arrays are the same length, and every index has the same value in both arrays. "Same value" in this
 *         case means "===" equality. Also true if both arrays are null. Otherwise, returns false.
 */
export function shallowEquals(arrayA: any[], arrayB: any[]): boolean {
    if (arrayA === arrayB) {
        return true;
    }

    if (arrayA == null || arrayB == null || arrayA.length !== arrayB.length) {
        return false;
    }

    for (let i = 0, len = arrayA.length; i < len; ++i) {
        if (arrayA[i] !== arrayB[i]) {
            return false;
        }
    }

    return true;
}

/**
 * Take an array of values and convert it to a dictionary/lookup table.
 * @param array Values to convert
 * @param getKey Function to get the key for a given item
 * @param getValue Optional function to get teh value for a given item (defaults to the item itself)
 * @param throwOnDuplicateKeys Optional value indicating to throw an error when duplicate keys are present. Otherwise just overwrite any duplicates
 * @return
 */
export function toDictionary<TArray, TValue>(
    array: TArray[],
    getKey: (item: TArray, index: number) => string,
    getValue?: (item: TArray, index: number) => TValue,
    throwOnDuplicateKeys?: boolean
): { [key: string]: TValue } {
    var lookup: { [key: string]: TValue } = {};
    array.forEach((item: TArray, index: number) => {
        var key = getKey(item, index);
        if (key) {
            var value: TValue;
            if (getValue) {
                value = getValue(item, index);
            } else {
                value = <any>item;
            }

            if (throwOnDuplicateKeys && lookup[key]) {
                // Debug message, should not be surfaced to customer, no loc needed.
                throw new Error("toDictionary: Duplicate entries for key: " + key);
            }

            lookup[key] = value;
        }
    });
    return lookup;
}

/**
 * @param array
 * @param predicate
 * @return
 */
export function findIndex<T>(array: T[], predicate: (param: T) => boolean): number {
    for (var i = 0, l = array.length; i < l; i++) {
        if (predicate(array[i])) {
            return i;
        }
    }

    return -1;
}

/**
 * Sorts and removes duplicate elements
 *
 * @param array array to sort 
 * @param comparer comparer that will be used for sorting
 * @return sorted array without duplicate elements
 */
export function uniqueSort<T>(array: T[], comparer: { (a: T, b: T): number }): T[] {
    array.sort(comparer);

    for (var i = 1, l = array.length; i < l; i++) {
        if (comparer(array[i], array[i - 1]) === 0) {
            array.splice(i--, 1);
            l--;
        }
    }

    return array;
}
